import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthModal, useAuthModal } from './components/Auth/AuthModal';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CategoryProvider } from './contexts/CategoryContext';
import { AnalyticsClient } from './analytics/AnalyticsClient';
import { useAnalytics } from './analytics/useAnalytics';
import { navigationService } from './utils/navigationService';

// Import your migrated components
import LandingPage from './pages/Landing/LandingPage';
import PasswordResetConfirm from './pages/Auth/PasswordResetConfirm';
import VerifyEmail from './pages/Auth/VerifyEmail';
import TermsOfService from './pages/Legal/TermsOfService';
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';
import DataDeletion from './pages/Legal/DataDeletion';
import { NavigationSetup } from './components/NavigationSetup';
import { useAppState } from './hooks/useAppState';
import { ScrollToTop } from './components/ScrollToTop/ScrollToTop';
import Spinner from '@/components/ui/Spinner';

// Admin tooling ships only in a separate build (REACT_APP_ENABLE_ADMIN=true,
// deployed to its own host) - the public site build contains none of it.
const ENABLE_ADMIN = process.env.REACT_APP_ENABLE_ADMIN === 'true';
const Login = process.env.REACT_APP_ENABLE_ADMIN === 'true' ? require('./pages/Login/Login').Login : null;
const ModerationDashboard = process.env.REACT_APP_ENABLE_ADMIN === 'true' ? require('./pages/Admin/ModerationDashboard').default : null;

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <Spinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Admin-only route. No nav-bar entry - reached by direct URL only.
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return <Spinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/explore" replace />;
  }

  return <>{children}</>;
};

// Component that uses auth interceptor inside Router context
function AppContent() {
  const { isAuthModalOpen, authModalConfig, openAuthModal, closeAuthModal, handleAuthSuccess } = useAuthModal();
  const { isAuthenticated, authResolved } = useAuth();
  const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000');
  const analyticsClient = React.useMemo(
    () => new AnalyticsClient(apiBase),
    [apiBase]
  );
  const location = useLocation();
  const routePath = location.pathname;
  useAnalytics(analyticsClient, routePath, authResolved, isAuthenticated);
  
  // Handle app state changes (background/foreground) for auth re-validation
  useAppState();
  
  // Register reload callback for navigation service
  React.useEffect(() => {
    navigationService.setReloadCallback(() => {
      // On web: reload the page
      // On mobile: trigger state refresh (handled by app state handler)
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    });
  }, []);

  // Listen for authentication errors and show modal instead of redirecting
  React.useEffect(() => {
    const handleAuthError = (event: CustomEvent) => {
      if (event.detail?.type === 'AUTH_ERROR') {
        openAuthModal({
          title: event.detail.title || "Authentication Required",
          message: event.detail.message || "Please log in to continue."
        });
      }
    };

    window.addEventListener('auth-error', handleAuthError as EventListener);
    
    return () => {
      window.removeEventListener('auth-error', handleAuthError as EventListener);
    };
  }, [openAuthModal]);

  return (
    <div className="App">
      {/* Scroll to top on route change */}
      <ScrollToTop />
      {/* Navigation Setup - registers navigation callbacks */}
      <NavigationSetup />
      {/* App Routes */}
      <Routes>
        {/* The web app is retired (Barkada is iOS-only). Only the marketing
            page, legal pages, emailed-link targets and the admin tools remain. */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/reset-password" element={<PasswordResetConfirm />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/data-deletion" element={<DataDeletion />} />

        {ENABLE_ADMIN && Login && ModerationDashboard && (
          <>
            <Route path="/login" element={<Login />} />
            <Route path="/admin/moderation" element={
              <AdminRoute>
                <ModerationDashboard />
              </AdminRoute>
            } />
          </>
        )}

        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Auth Modal for in-app authentication */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={closeAuthModal}
        onSuccess={handleAuthSuccess}
        title={authModalConfig.title}
        message={authModalConfig.message}
      />

      {/* Toast notifications */}
      <Toaster 
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#4ade80',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <CategoryProvider>
        <Router 
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <AppContent />
        </Router>
      </CategoryProvider>
    </AuthProvider>
  );
}

export default App;
